import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  makeStyles,
  Chip,
  InputAdornment,
  Divider,
  Grid,
} from "@material-ui/core";
import { getLang } from "app/feature/constants";
import AntSwitch from "components/switch";
import { Formik, setNestedObjectValues } from "formik";
import { Skeleton } from "@material-ui/lab";
import InputTextField from "components/input/inputTextField";
import RefreshTable from "components/table/refreshTable";
import { currencies } from "lib/constants/currency";

const useStyle = makeStyles((theme) => ({
  inputRoot: {
    paddingTop: "8px !important",
    paddingBottom: "7px !important",
  },
  cardContainer: {
    overflow: "hidden",
    border: "1px solid #e9e9e9",
    borderRadius: 8,
    "& > div:not(:last-child)": {
      borderBottom: "1px solid #e9e9e9",
    },
    "& > div:nth-of-type(odd)": {
      padding: 16,
      backgroundColor: "#ffffff",
    },
    "& > div:nth-of-type(even)": {
      padding: 16,
      backgroundColor: "#F9FAFB",
    },
  },
}));

export default function CreditReloadLogPanelComponent({
  lang,
  alertInfo,
  isFetching,
  isError,
  handleReload,
  isEdit,
  setIsEdit,
  handleSubmit,
  isSubmitting,
  disbursementInfo,
  canEditCreditAlert,
}) {
  const classes = useStyle();

  const exp = useMemo(() => {
    if (disbursementInfo?.currency) {
      return (
        currencies.find((c) => c.alpha3 === disbursementInfo.currency)?.exp ?? 2
      );
    }
    return 2;
  }, [disbursementInfo]);

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isError ? (
        <Box>
          <Box>
            <Typography variant="body1">
              <b>{getLang(lang, "label.CREDIT_ALERT")}</b>
            </Typography>
          </Box>
          <Box mt={1} mb={2}>
            <Divider />
          </Box>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RefreshTable handleReload={handleReload} />
          </Box>
        </Box>
      ) : (
        <Formik
          initialValues={{
            alertThreshold: alertInfo.alertThreshold.toFixed(exp),
            hasAlert: alertInfo.hasAlert,
            newRecipient: "",
            recipients: alertInfo.recipients,
            transactionThreshold: alertInfo.transactionThreshold,
          }}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gridGap={16}
              >
                <Box>
                  <Typography variant="body1">
                    <b>{getLang(lang, "label.CREDIT_ALERT")}</b>
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  {isEdit ? (
                    <>
                      <Button
                        color="primary"
                        variant="text"
                        disableElevation
                        onClick={() => {
                          setIsEdit(false);
                          formik.resetForm();
                        }}
                        style={{ marginRight: "4px" }}
                      >
                        {getLang(lang, "label.CANCEL")}
                      </Button>
                      <Button
                        color="primary"
                        variant="contained"
                        disableElevation
                        type="submit"
                        disabled={isSubmitting}
                        onClick={async () => {
                          const errors = await formik.validateForm();

                          if (Object.keys(errors).length === 0) {
                            formik.handleSubmit();
                          } else {
                            formik.setTouched(
                              setNestedObjectValues(errors, true)
                            );
                          }
                        }}
                      >
                        {isSubmitting ? (
                          <CircularProgress
                            size={20}
                            style={{ color: "white" }}
                          />
                        ) : (
                          <Typography>
                            {getLang(lang, "label.SUBMIT")}
                          </Typography>
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      color="primary"
                      variant="contained"
                      disableElevation
                      onClick={() => setIsEdit(true)}
                      disabled={isFetching || !canEditCreditAlert}
                    >
                      {getLang(lang, "label.UPDATE")}
                    </Button>
                  )}
                </Box>
              </Box>
              <Box mt={1} mb={2}>
                <Divider />
              </Box>
              <form
                onSubmit={formik.handleSubmit}
                style={{ height: "inherit" }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "inherit",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Box className={classes.cardContainer} mt={1}>
                      <Box>
                        <Typography variant="body2">
                          <b>
                            {isEdit
                              ? `${getLang(lang, "label.ALERT_RECIPIENTS")} (
                                ${getLang(lang, "label.MAX_COUNT_RECIPIENTS", {
                                  count: 5,
                                })}
                              )`
                              : getLang(lang, "label.ALERT_RECIPIENTS")}
                          </b>
                          {isEdit && (
                            <span style={{ color: "#FF3333" }}>&nbsp;*</span>
                          )}
                        </Typography>
                      </Box>
                      {isFetching ? (
                        <Box>
                          <Skeleton
                            variant="text"
                            animation="wave"
                            height={25}
                          />
                          <Skeleton
                            variant="text"
                            animation="wave"
                            height={25}
                          />
                          <Skeleton
                            variant="text"
                            animation="wave"
                            height={25}
                          />
                        </Box>
                      ) : isEdit ? (
                        <Box>
                          <Box display="flex" flexWrap="wrap">
                            {formik.values.recipients.length > 0
                              ? formik.values.recipients.map((email) => (
                                  <Box
                                    key={email}
                                    style={{
                                      marginRight: 8,
                                      marginBottom: 8,
                                    }}
                                  >
                                    <Chip
                                      label={email}
                                      onDelete={() => {
                                        let temp =
                                          formik.values.recipients.filter(
                                            (war) => war !== email
                                          );
                                        formik.setFieldValue(
                                          "recipients",
                                          temp
                                        );
                                      }}
                                      disabled={isSubmitting}
                                    />
                                  </Box>
                                ))
                              : null}
                          </Box>
                          <InputTextField
                            variant="filled"
                            size="small"
                            fullWidth
                            placeholder={getLang(
                              lang,
                              "placeholder.ENTER_EMAIL"
                            )}
                            InputProps={{
                              style: {
                                padding: "4px",
                                backgroundColor: "#ECEFF0",
                                borderRadius: "4px 0px 0px 4px",
                              },
                              classes: { input: classes.inputRoot },
                            }}
                            onChange={(e) => {
                              formik.setFieldValue(
                                "newRecipient",
                                e.target.value
                              );
                            }}
                            onKeyPress={(e) => {
                              if (
                                (e.key === "Enter" || e.key === ";") &&
                                formik.values.newRecipient !== ""
                              ) {
                                e.preventDefault();
                                let errorMsg = "";
                                let isError = false;
                                let array = [formik.values.newRecipient.trim()];
                                let arrayToInsert = [];

                                if (array.length !== 0) {
                                  array.forEach((input) => {
                                    let val = input.trim();
                                    if (!formik.errors.newRecipient) {
                                      if (formik.values.recipients) {
                                        const isValidEmail =
                                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\s*;\s*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*$/.test(
                                            val
                                          );

                                        if (isValidEmail) {
                                          if (
                                            formik.values.recipients.filter(
                                              (email) => email === val
                                            ).length === 0 &&
                                            arrayToInsert.filter(
                                              (value) => value === val
                                            ).length === 0
                                          ) {
                                            arrayToInsert = [
                                              ...arrayToInsert,
                                              val,
                                            ];
                                          } else {
                                            isError = true;
                                            errorMsg = getLang(
                                              lang,
                                              "message.error.ERROR_DUPLICATE_EMAIL"
                                            );
                                          }
                                        } else {
                                          isError = true;
                                          errorMsg = getLang(
                                            lang,
                                            "message.error.FORM_VALIDATE_EMAIL"
                                          );
                                        }
                                      } else {
                                        isError = true;
                                        errorMsg = getLang(
                                          lang,
                                          "message.error.FORM_VALIDATE_EMAIL"
                                        );
                                      }
                                    }
                                  });
                                }
                                if (arrayToInsert.length !== 0 && !isError) {
                                  formik.setFieldValue("recipients", [
                                    ...formik.values.recipients,
                                    ...arrayToInsert,
                                  ]);
                                  formik.setFieldValue("newRecipient", "");
                                }
                                formik.setFieldError("newRecipient", errorMsg);
                              }
                            }}
                            error={formik.errors.newRecipient ? true : false}
                            helperText={formik.errors.newRecipient}
                            {...formik.getFieldProps("newRecipient")}
                            disabled={
                              isSubmitting ||
                              formik.values.recipients.length >= 5
                            }
                          />
                          <Box>
                            <Typography
                              variant="caption"
                              style={{ color: "#98A2B3" }}
                            >
                              {getLang(
                                lang,
                                "paragraph.ENTER_MULTIPLE_EMAIL_WITH_SEPARATOR",
                                {
                                  separatorName: getLang(
                                    lang,
                                    "label.SEMICOLUN"
                                  ).toLowerCase(),
                                  separator: ";",
                                }
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          {formik.values.recipients.length ? (
                            formik.values.recipients.map((r, index) => (
                              <Box key={index} mt={index ? 1 : 0}>
                                <Typography variant="body2">{r}</Typography>
                              </Box>
                            ))
                          ) : (
                            <Box display="flex" justifyContent="center">
                              <Typography variant="body2">
                                {getLang(lang, "paragraph.NO_RECIPIENT_SET")}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                    <Box className={classes.cardContainer} mt={3}>
                      <Box>
                        <Typography variant="body2">
                          <b>
                            {getLang(lang, "label.LOW_CREDIT_BALANCE_ALERT")}
                          </b>
                        </Typography>
                      </Box>
                      {isFetching ? (
                        <>
                          <Skeleton
                            variant="text"
                            animation="wave"
                            height={25}
                          />
                          <Skeleton
                            variant="text"
                            animation="wave"
                            height={25}
                          />
                          <Skeleton
                            variant="text"
                            animation="wave"
                            height={25}
                          />
                        </>
                      ) : isEdit ? (
                        <>
                          <Box>
                            <Box display="flex" alignItems="center">
                              <Box mr={1} display="flex">
                                <AntSwitch
                                  checked={formik.values.hasAlert}
                                  onChange={(e) => {
                                    let newVal = e.target.checked;
                                    formik.setFieldValue("hasAlert", newVal);

                                    if (!newVal) {
                                      formik.setFieldValue(
                                        "alertThreshold",
                                        alertInfo.alertThreshold
                                      );
                                    }
                                  }}
                                  disabled={isSubmitting}
                                />
                              </Box>
                              <Typography variant="body2">
                                {getLang(
                                  lang,
                                  "label.LOW_CREDIT_BALANCE_ALERT"
                                )}
                              </Typography>
                            </Box>
                            {formik.values.hasAlert && (
                              <>
                                <Box mt={3}>
                                  <Box mb={0.5}>
                                    <Typography
                                      variant="body2"
                                      style={{ color: "#596780" }}
                                    >
                                      {getLang(
                                        lang,
                                        "label.LOW_BALANCE_AMOUNT"
                                      )}
                                    </Typography>
                                  </Box>
                                  <InputTextField
                                    variant="filled"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    placeholder={getLang(
                                      lang,
                                      "placeholder.AMOUNT"
                                    )}
                                    InputProps={{
                                      style: {
                                        padding: "4px",
                                        backgroundColor: "#ECEFF0",
                                        borderRadius: "4px 0px 0px 4px",
                                      },
                                      classes: { input: classes.inputRoot },
                                      startAdornment: (
                                        <InputAdornment>
                                          {disbursementInfo
                                            ? disbursementInfo.currency
                                            : null}
                                        </InputAdornment>
                                      ),
                                    }}
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        "alertThreshold",
                                        e.target.value
                                      );
                                    }}
                                    onKeyPress={(e) => {
                                      const disabledKey = [
                                        "e",
                                        "E",
                                        ".",
                                        "+",
                                        "-",
                                      ];
                                      if (e.key.includes(disabledKey)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    error={
                                      formik.errors.alertThreshold
                                        ? true
                                        : false
                                    }
                                    helperText={formik.errors.alertThreshold}
                                    {...formik.getFieldProps("alertThreshold")}
                                    disabled={isSubmitting}
                                  />
                                </Box>
                                <Box mt={1}>
                                  <Box mb={0.5}>
                                    <Typography
                                      variant="body2"
                                      style={{ color: "#596780" }}
                                    >
                                      {getLang(
                                        lang,
                                        "label.LOW_TRANSACTION_AMOUNT"
                                      )}
                                    </Typography>
                                  </Box>
                                  <InputTextField
                                    variant="filled"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    placeholder={getLang(
                                      lang,
                                      "placeholder.AMOUNT"
                                    )}
                                    InputProps={{
                                      style: {
                                        padding: "4px",
                                        backgroundColor: "#ECEFF0",
                                        borderRadius: "4px 0px 0px 4px",
                                      },
                                      classes: { input: classes.inputRoot },
                                    }}
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        "transactionThreshold",
                                        e.target.value
                                      );
                                    }}
                                    onKeyPress={(e) => {
                                      const disabledKey = [
                                        "e",
                                        "E",
                                        ".",
                                        "+",
                                        "-",
                                      ];
                                      if (e.key.includes(disabledKey)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    error={
                                      formik.errors.transactionThreshold
                                        ? true
                                        : false
                                    }
                                    helperText={
                                      formik.errors.transactionThreshold
                                    }
                                    {...formik.getFieldProps(
                                      "transactionThreshold"
                                    )}
                                    disabled={isSubmitting}
                                  />
                                </Box>
                              </>
                            )}
                          </Box>
                        </>
                      ) : formik.values.hasAlert ? (
                        <>
                          <Box display="flex" alignItems="center">
                            <Grid container>
                              <Grid item xs={7}>
                                <Typography
                                  variant="body2"
                                  style={{ color: "#596780" }}
                                >
                                  {getLang(lang, "label.LOW_BALANCE_AMOUNT")}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" align="right">
                                  {disbursementInfo
                                    ? disbursementInfo.currency
                                    : null}
                                  {Number(
                                    formik.values.alertThreshold
                                  )?.toLocaleString("en-US", {
                                    minimumFractionDigits: exp,
                                    maximumFractionDigits: exp,
                                  })}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                          <Box display="flex" alignItems="center">
                            <Grid container>
                              <Grid item xs={7}>
                                <Typography
                                  variant="body2"
                                  style={{ color: "#596780" }}
                                >
                                  {getLang(
                                    lang,
                                    "label.LOW_TRANSACTION_AMOUNT"
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" align="right">
                                  {formik.values.transactionThreshold.toLocaleString(
                                    "en-US"
                                  )}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        </>
                      ) : (
                        <Box display="flex" justifyContent="center">
                          <Typography variant="body2">
                            {getLang(lang, "label.ALERT_DISABLED")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </form>
            </>
          )}
        </Formik>
      )}
    </Box>
  );
}
